import { useState, useCallback, useEffect } from 'react';
import { SingleFileUploader } from '@commenda/kakashi';
import type { FileBasic } from '@commenda/types/src/file/types';
import { uploadSalesTaxFile } from '@commenda/api/new-sales-tax/files/upload';
import { getSalesTaxFile } from '@commenda/api/new-sales-tax/files/get';

interface IndirectTaxCredentialFileUploadProps {
  fieldKey: string;
  fieldName: string;
  helperText?: string;
  required: boolean;
  value: string;
  onChange: (key: string, value: string) => void;
  corporationId: number;
  companyId?: number;
}

export function IndirectTaxCredentialFileUpload({
  fieldKey,
  fieldName,
  helperText,
  required,
  value,
  onChange,
  corporationId,
  companyId,
}: IndirectTaxCredentialFileUploadProps) {
  const [file, setFileInternal] = useState<FileBasic | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Wrap setFile so deleting the file also clears the form value
  const setFile = useCallback(
    (newFile: FileBasic | null) => {
      setFileInternal(newFile);
      if (!newFile) {
        onChange(fieldKey, '');
      }
    },
    [fieldKey, onChange]
  );

  // Prefill the file state from existing file ID so SingleFileUploader shows its preview card
  useEffect(() => {
    if (!value || file) return;

    getSalesTaxFile(value, corporationId).then((result) => {
      if (result.success && result.data?.data) {
        setFileInternal({
          fileName: result.data.data.name,
          fileUrl: result.data.data.signed_url || '',
          documentType: 'Other',
        });
      }
    });
  }, [value, corporationId, file]);

  const handleFileChange = useCallback(
    async (uploadedFile: FileBasic) => {
      if (!uploadedFile?.fileUrl || !uploadedFile?.fileName) return;

      setUploadError(null);

      try {
        // Upload to sales-tax API via logical backend — gets sales-tax file ID
        const result = await uploadSalesTaxFile({
          file: uploadedFile,
          corporation_id: corporationId,
        });

        if (!result.success || !result.data?.data?.id) {
          throw new Error(result.error || 'Failed to upload file');
        }

        // Store the sales-tax file ID as the credential value
        onChange(fieldKey, result.data.data.id);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'File upload failed';
        setUploadError(message);
        setFile(null);
      }
    },
    [fieldKey, onChange, corporationId]
  );

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-xxs">
        <p className="txt-small-bold text-text-secondary">
          {fieldName}
          {required ? (
            <span className="text-utility-violet-600 ml-0.5">*</span>
          ) : (
            <span className="txt-small-regular text-text-tertiary ml-1">(optional)</span>
          )}
        </p>
        {helperText && (
          <p className="txt-extra-small-regular text-text-tertiary">{helperText}</p>
        )}
      </div>
      <SingleFileUploader
        // Safe: file is FileBasic | null; the component handles the null case internally
        file={file as FileBasic}
        setFile={setFile}
        companyId={companyId}
        documentType="Other"
        onFileChange={handleFileChange}
      />
      {uploadError && (
        <p className="txt-tiny-regular text-utility-error-600">{uploadError}</p>
      )}
    </div>
  );
}
